import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import { getProgressColor } from './../../../../utilities/getStatusColor';

import './styles.scss';
import classnames from 'classnames';

type PropsType = {
    progress?: number | null;
    size?: number;
    strokeWidth?: number;
    showValue?: boolean;
};

const Progress = ({ size, progress, color, strokeWidth }: { size: number, progress: number, color: string, strokeWidth: number }) => {
    const radius = (size - strokeWidth) / 2;
    const dashArray = radius * Math.PI * 2;
    const [dashOffset, setDashOffset] = useState(dashArray);

    useEffect(() => {
        const finalOffset = dashArray - (dashArray * progress) / 100;
        const timeout = setTimeout(() => {
            setDashOffset(finalOffset);
        }, 50);
        return () => clearTimeout(timeout);
    }, [progress, dashArray]);

    return (
        <circle
            fill="transparent"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
            strokeLinejoin="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{
                transition: 'stroke-dashoffset 0.25s ease-out',
                strokeDasharray: dashArray,
                strokeDashoffset: dashOffset,
            }}
        />
    );
};

export const PPDWidgetItemValueProgress = observer(({ progress, size = 152, strokeWidth = 24, showValue = true }: PropsType) => {
    const isValidProgress = progress !== undefined && progress !== null && !isNaN(progress);
    const radius = (size - strokeWidth) / 2;

    if (!isValidProgress) {
        return (
            <div
                className="ppd-widget-item-value-progress"
                style={{ width: size, height: size }}
            >
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    <circle
                        fill="transparent"
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        strokeWidth={strokeWidth}
                        stroke="#EAECF1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                <div className="ppd-widget-item-value-progress__value">—</div>
            </div>
        );
    }

    const isOver200 = progress >= 200;
    const isOver100 = progress > 100 && !isOver200;
    const color = isOver100 ? '#15803D' : getProgressColor(progress, 'transparent');
    const backgroundColor = isOver100 ? '#22C55E' : '#E9EBF1';

    const progressKey =
        isOver200 ? 'over200' :
            isOver100 ? 'over' :
                progress === 100 ? 'full' :
                    'base';

    const displayProgress = isOver200
        ? 100
        : isOver100
            ? progress % 100
            : progress;

    return (
        <div className="ppd-widget-item-value-progress" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                    fill="transparent"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    stroke={backgroundColor}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                <Progress
                    key={progressKey}
                    progress={displayProgress}
                    size={size}
                    color={color}
                    strokeWidth={strokeWidth}
                />
            </svg>

            {
                showValue && (
                    <div
                        className={classnames('ppd-widget-item-value-progress__value', {
                            'ppd-widget-item-value-progress__value--equal-100': progress >= 100,
                        })}
                    >
                        {progress}
                        <span
                            className={classnames('ppd-widget-item-value-progress__percent', {
                                'ppd-widget-item-value-progress__percent--equal-100': progress >= 100,
                            })}
                        >
                            %
                        </span>
                    </div>
                )
            }
        </div>
    );
});
